import React, { useEffect } from 'react'
import styled from 'styled-components'
import SliderReactSlick from 'react-slick'
import { AspectRatioWrapper } from 'fbk-components'
import { Lens as LensIcon, ArrowSlide as ArrowSlideIcon } from 'assets/svg'
import { SliderProps } from './types'

const StyledAspectRatioWrapper = styled(AspectRatioWrapper)`
  height: 100%;

  .image-wrap {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
      border: 1px solid rgba(0, 11, 66, 0.1);
    }
  }
`

const Slide = styled.div`
  .image {
    @media (max-width: 767px) {
      box-shadow: 0 0 60px rgba(157, 167, 217, 0.2);
    }
  }
`

const LeftArea = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  width: 30%;
  height: 100%;
  cursor: pointer;

  &:hover {
    .prev-button {
      opacity: 1;
    }
  }
`

const RightArea = styled(LeftArea)`
  right: 0;
  left: auto;
  justify-content: flex-end;

  &:hover {
    .next-button {
      opacity: 1;
    }
  }
`

const CenterArea = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  cursor: pointer;

  @media (min-width: 768px) {
    right: 30%;
    bottom: 0;
    left: 30%;
    width: auto;
    height: auto;

    .lens {
      opacity: 0;
    }

    &:hover {
      .lens {
        opacity: 1;
      }
    }
  }
`

const Markers = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;

  @media (min-width: 768px) {
    margin-top: 16px;
  }

  .marker {
    width: 8px;
    height: 8px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.grey};

    &:not(:last-child) {
      margin-right: 8px;
    }
  }

  .active {
    width: 24px;
    cursor: initial;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`

interface Props extends SliderProps {
  openModal?: () => void
  isInModal?: boolean
  className?: string
}

function Slider({
  images,
  activeSlide,
  setActiveSlide,
  isSmallScreen,
  isLargeScreen,
  openModal,
  isInModal,
  className,
}: Props) {
  let slider: SliderReactSlick | null = null
  const isLastSlide = images.length === activeSlide + 1
  const isFirstSlide = activeSlide === 0
  const lastSlide = images.length - 1

  useEffect(() => {
    if (slider) {
      slider.slickGoTo(activeSlide)
    }
  }, [activeSlide, slider])

  const commonSettings = {
    dots: false,
    slidesToShow: 1,
    slidesToScroll: 1,
  }

  const mobileSettings = {
    speed: 500,
    infinite: true,
    onSwipe: (direction: string) => {
      if (direction === 'left') {
        setActiveSlide(isLastSlide ? 0 : activeSlide + 1)
      } else if (direction === 'right') {
        setActiveSlide(isFirstSlide ? lastSlide : activeSlide - 1)
      }
    },
  }

  const desktopSettings = {
    accessibility: true,
    speed: isLargeScreen ? 0 : 300,
    infinite: true,
    swipe: false,
    afterChange: (index: number) => {
      setActiveSlide(index)
    },
  }

  const settings = isSmallScreen
    ? { ...commonSettings, ...mobileSettings }
    : { ...commonSettings, ...desktopSettings }

  function handlePrevSlide() {
    if (!slider) return

    slider.slickPrev()
  }

  function handleNextSlide() {
    if (!slider) return

    slider.slickNext()
  }

  return (
    <div className={className}>
      <div className="carousel">
        <SliderReactSlick ref={c => (slider = c)} {...settings}>
          {images.map((url: string, index: number) => (
            <Slide key={index}>
              {isInModal ? (
                <div className="image-wrap">
                  <img src={url} alt="Слайд" />
                </div>
              ) : (
                <StyledAspectRatioWrapper aspectRatio="16 / 9">
                  <div className="image-wrap">
                    <img src={url} alt="Слайд" />
                  </div>
                </StyledAspectRatioWrapper>
              )}
            </Slide>
          ))}
        </SliderReactSlick>
        {!isSmallScreen && (
          <LeftArea onClick={handlePrevSlide} className="left-area">
            <div className="prev-button">
              <ArrowSlideIcon />
            </div>
          </LeftArea>
        )}
        {openModal && (
          <CenterArea onClick={openModal}>
            <div className="lens">
              <LensIcon />
            </div>
          </CenterArea>
        )}
        {!isSmallScreen && (
          <RightArea onClick={handleNextSlide} className="right-area">
            <div className="next-button">
              <ArrowSlideIcon />
            </div>
          </RightArea>
        )}
      </div>
      {!isLargeScreen && (
        <Markers className="markers">
          {images.map((_: any, index: number) => {
            return (
              <div
                key={index}
                onClick={() => setActiveSlide(index)}
                className={`marker ${index === activeSlide ? 'active' : ''}`}
              />
            )
          })}
        </Markers>
      )}
    </div>
  )
}

const StyledSlider = styled(Slider)`
  .slick-slide {
    @media (max-width: 767px) {
      padding: 0 8px;
    }
  }

  .carousel {
    position: relative;

    @media (max-width: 767px) {
      margin: 0 -8px;
    }
  }

  .slick-slider button {
    display: none !important;
    cursor: pointer;
  }

  .lens {
    position: absolute;
    top: calc(50% - 20px);
    right: calc(50% - 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #fff;
    transition: 0.2s ease-in-out;
  }

  .prev-button,
  .next-button {
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    user-select: none;
    background: #fff;
    opacity: 0;
    transition: 0.2s ease-in-out;
  }

  .prev-button {
    position: relative;
    left: 0;
    transform: rotate(180deg);
  }

  .next-button {
    right: 0;

    img {
      position: relative;
      left: 1px;
    }
  }

  .desc {
    line-height: 24px;
    color: ${({ theme }) => theme.grey};
    text-align: center;
  }
`

export default StyledSlider
